/**
 * Безопасные обёртки над Telegram.WebApp. Вне Telegram (обычный браузер) все методы — no-op
 * (или браузерный фолбэк: openTelegramLink → window.open, shareUrl → navigator.share/clipboard).
 *
 *   import telegram from '@/services/telegram'
 *   telegram.init()                       // один раз при старте (App.vue)
 *   telegram.isTelegram                   // true внутри Telegram (есть initData)
 *   telegram.initData()                   // строка initData или ''
 *   telegram.platform                     // 'ios' | 'android' | 'tdesktop' | … | 'unknown'
 *   telegram.haptic('light'|'medium'|'heavy'|'rigid'|'soft'|'success'|'warning'|'error'|'selection')
 *   telegram.backButton(true, handler)    // показать BackButton с обработчиком (предыдущий снимается)
 *   telegram.backButton(false)            // скрыть
 *   telegram.closingConfirmation(true)    // подтверждение закрытия (только во время игры)
 *   telegram.close()
 *   telegram.openTelegramLink(url)
 *   telegram.shareUrl(url, text)          // t.me/share/url
 */

const THEME_BG = "#0B0E0A";

function webApp() {
  return (typeof window !== "undefined" && window.Telegram && window.Telegram.WebApp) || null;
}

/** Внутри Telegram: есть WebApp и непустой initData. */
function inTelegram() {
  const wa = webApp();
  return !!(wa && wa.initData);
}

function supports(version) {
  const wa = webApp();
  if (!wa) return false;
  try {
    return typeof wa.isVersionAtLeast === "function" ? wa.isVersionAtLeast(version) : false;
  } catch (e) {
    return false;
  }
}

function safe(fn) {
  try {
    return fn();
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.warn("[telegram]", e);
    return undefined;
  }
}

let backHandler = null;

const telegram = {
  /** true внутри Telegram Mini App. */
  get isTelegram() {
    return inTelegram();
  },

  /** Платформа клиента Telegram ('unknown' вне Telegram). */
  get platform() {
    const wa = webApp();
    return (wa && wa.platform) || "unknown";
  },

  /** true на мобильных клиентах Telegram (ios/android). */
  get isMobile() {
    return ["ios", "android", "android_x"].includes(this.platform);
  },

  /** Инициализация: ready, expand, отключение свайпа-закрытия, цвета шапки/фона, портрет. */
  init() {
    const wa = webApp();
    if (!wa || !inTelegram()) return;
    safe(() => wa.ready());
    safe(() => wa.expand());
    if (supports("7.7")) safe(() => wa.disableVerticalSwipes());
    if (supports("6.1")) {
      safe(() => wa.setHeaderColor(THEME_BG));
      safe(() => wa.setBackgroundColor(THEME_BG));
    }
    if (supports("7.10")) safe(() => wa.setBottomBarColor(THEME_BG));
    if (supports("8.0") && this.isMobile) safe(() => wa.lockOrientation());
  },

  /** Сырой initData (строка) или ''. */
  /** Direct link payload, used only as navigation intent. */
  startParam() {
    return webApp()?.initDataUnsafe?.start_param || "";
  },

  initData() {
    const wa = webApp();
    return (wa && wa.initData) || "";
  },

  /**
   * Тактильный отклик.
   * @param {'light'|'medium'|'heavy'|'rigid'|'soft'|'success'|'warning'|'error'|'selection'} kind
   */
  haptic(kind = "light") {
    const wa = webApp();
    if (!wa || !inTelegram() || !supports("6.1") || !wa.HapticFeedback) return;
    safe(() => {
      if (kind === "selection") wa.HapticFeedback.selectionChanged();
      else if (["success", "warning", "error"].includes(kind)) wa.HapticFeedback.notificationOccurred(kind);
      else wa.HapticFeedback.impactOccurred(kind);
    });
  },

  /**
   * Показать/скрыть системную кнопку «Назад». Одновременно активен один обработчик.
   * @param {boolean} show
   * @param {Function} [handler]
   */
  backButton(show, handler) {
    const wa = webApp();
    if (!wa || !inTelegram() || !supports("6.1") || !wa.BackButton) return;
    safe(() => {
      if (backHandler) wa.BackButton.offClick(backHandler);
      backHandler = null;
      if (show) {
        if (typeof handler === "function") {
          backHandler = handler;
          wa.BackButton.onClick(backHandler);
        }
        wa.BackButton.show();
      } else {
        wa.BackButton.hide();
      }
    });
  },

  /** Включить/выключить подтверждение закрытия Mini App (только во время игры). */
  closingConfirmation(enabled) {
    const wa = webApp();
    if (!wa || !inTelegram() || !supports("6.2")) return;
    safe(() => (enabled ? wa.enableClosingConfirmation() : wa.disableClosingConfirmation()));
  },

  /** Закрыть Mini App. Возвращает false, если мы не в Telegram. */
  close() {
    const wa = webApp();
    if (!wa || !inTelegram()) return false;
    safe(() => wa.close());
    return true;
  },

  /** Открыть ссылку t.me внутри Telegram (вне Telegram — новая вкладка). */
  openTelegramLink(url) {
    const wa = webApp();
    if (wa && inTelegram() && typeof wa.openTelegramLink === "function") {
      safe(() => wa.openTelegramLink(url));
      return;
    }
    window.open(url, "_blank", "noopener");
  },

  /**
   * Поделиться ссылкой через Telegram (t.me/share/url). Вне Telegram — Web Share API,
   * затем копирование в буфер. Возвращает Promise<'telegram'|'shared'|'copied'|'failed'>.
   */
  async shareUrl(url, text = "") {
    if (inTelegram()) {
      const shareLink = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
      this.openTelegramLink(shareLink);
      return "telegram";
    }
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ url, text });
        return "shared";
      } catch (e) {
        if (e && e.name === "AbortError") return "failed";
      }
    }
    try {
      await navigator.clipboard.writeText(text ? `${text} ${url}` : url);
      return "copied";
    } catch (e) {
      return "failed";
    }
  },
};

export default telegram;
